import { useState, useEffect, useMemo, useRef } from "react";
import { useOutletContext, useSearchParams, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getAggregatePagesBreakdown,
  getAggregateClicksBreakdown,
  getPagesDetails,
  type PageBreakdown,
  type PagesDetailsResponse,
} from "@/lib/api/external-api";
import { interceptDemoAPI, demoGenerators } from "@/lib/utils/demoData";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import { useFilter } from "@/lib/contexts/FilterContext";
import { useURLState } from "@/hooks/useURLState";
import { usePreviousPeriodMetrics, computePeriodDelta } from "@/hooks/usePreviousPeriodMetrics";
import { formatNumeric, formatDuration } from "@/lib/utils/stringUtils";
import { formatUtmLabel } from "@/lib/utils/utmLabel";
import { cn } from "@/lib/utils";
import { TrafficSourcesList } from "@/components/shared/TrafficSourcesList";
import { TooltipProvider } from "@/components/ui/tooltip";
import { matchesPageSearch } from "@/lib/utils/pagePath";
import AnalyticsTabBar from "@/components/dashboard/AnalyticsTabBar";
import { uniqueForGroupSelection } from "@/lib/calculations/breakdownTotals";
import { addFilter, removeFilter } from "@/lib/routing/filter-dsl";
import type { FilterClause } from "@/lib/routing/types";
import { getTrafficSummary } from "@/components/shared/charts/TrafficMetricsCard";
import { formatRangeLabel, periodNoun } from "@/lib/utils/analyticsRange";
import {
  buildFilteredLandingSegments,
  buildLandingSegments,
  findNearestPage,
  getActivePageFilter,
} from "@/lib/calculations/pagesReport";
import {
  ReportPagination,
  ShareMeter,
  reportTable,
} from "@/components/shared/analytics/AnalyticsReport";
import {
  PagesFilterEmpty,
  PagesListToolbar,
  PagesLoading,
  PagesSummaryCard,
  PagesTableHead,
  PagesTopTable,
  PagesVerdict,
  SectionChip,
  SectionChipRow,
  sentenceLabel,
  WhereViewsLand,
  type PageListView,
  type PagesVerdictProps,
} from "@/components/shared/analytics/PagesReport";

// Convert frontend period format to external API format
const toExternalApiPeriod = (period: string): string => {
  // Handle custom date range format: "YYYY-MM-DD:YYYY-MM-DD"
  if (period.includes(":")) {
    const [start, end] = period.split(":");
    // Calculate the difference to determine granularity
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffDays = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Choose granularity based on date range length
    let granularity = "day";
    if (diffDays <= 2) {
      granularity = "hour";
    } else if (diffDays > 90) {
      granularity = "week";
    }

    // Format: granularity_CUSTOM_start_to_end (matching API expectation)
    return `${granularity}_CUSTOM_${start}_to_${end}`;
  }

  const periodMap: Record<string, string> = {
    "1d": "hour_PAST_24_H",
    "7d": "day_PAST_7_D",
    "30d": "day_PAST_30_D",
    "90d": "day_PAST_3_M",
    "180d": "week_PAST_6_M",
    "365d": "week_PAST_12_M",
    "12m": "month_PAST_12_M",
    ytd: "YEAR_TO_DATE",
    mtd: "MONTH_TO_DATE",
  };
  return periodMap[period] || "day_PAST_7_D";
};


// Matches the main fetch, so the nearest-page search sees the same top pages.
const PAGE_BATCH_SIZE = 100;

interface DashboardContext {
  propertyId?: string;
  token?: string;
  period?: string;
  filters?: any[];
  dashboardData?: any;
  isLoading?: boolean;
}

type PageViewType = PageListView;

interface EntryExitPage {
  page: string;
  entry_count: string;
}

type PageClickTotals = { clicks: number };

type ClicksJoinCursor = {
  offset: number;
  total: number;
  exhausted: boolean;
};

function mergeClicksIntoMap(
  map: Record<string, PageClickTotals>,
  rows: Array<{ page: string; total_clicks: number }>,
) {
  for (const row of rows) {
    map[row.page] = { clicks: row.total_clicks };
  }
}

/**
 * Walk the clicks breakdown to exhaustion, merging every page's click total into
 * the map. The pages and clicks endpoints rank by different metrics (page_views vs
 * total_clicks), so the clicks row for a visible page can sit anywhere in the clicks
 * list — reading only as far as the loaded pages reach would leave visible, clicked
 * pages showing no CTR. The clicks list only contains pages that actually have clicks
 * (zero-click pages never appear), so this is bounded by pages-with-clicks, not the
 * whole catalog. Each merged batch is emitted via `onBatch` so CTR fills in
 * progressively, and `isStale` lets a superseding refresh cancel it mid-walk.
 */
async function advanceClicksJoin(args: {
  map: Record<string, PageClickTotals>;
  cursor: ClicksJoinCursor;
  propertyIds: string[];
  period: string;
  token: string;
  sharePropertyId?: string;
  filterParams: Record<string, string>;
  search?: string;
  batchSize: number;
  isStale: () => boolean;
  onBatch: (map: Record<string, PageClickTotals>, cursor: ClicksJoinCursor) => void;
}): Promise<void> {
  const map = { ...args.map };
  let { offset, total, exhausted } = args.cursor;

  while (!exhausted) {
    if (args.isStale()) return;
    try {
      const response = await getAggregateClicksBreakdown(
        args.propertyIds,
        args.period,
        args.token,
        args.batchSize,
        args.sharePropertyId,
        args.filterParams,
        args.search,
        offset,
      );
      const rows = response.data ?? [];
      mergeClicksIntoMap(map, rows);
      offset += rows.length;
      total = response.meta.total_count ?? offset;
      exhausted = rows.length === 0 || offset >= total;
    } catch (error) {
      console.error("Error fetching page click totals:", error);
      exhausted = true;
    }
    if (args.isStale()) return;
    args.onBatch({ ...map }, { offset, total, exhausted });
  }
}

export default function Pages() {
  const ctx = useOutletContext<DashboardContext>();
  const params = useParams();
  const { token: authToken } = useAuth();
  const { filters: contextFilters } = useFilter();
  const { state, updateState } = useURLState();

  // Use token from context (share mode) or auth (regular mode)
  const token = ctx.token || authToken;
  const workspaceContext = useWorkspaceSafe();
  const propertyIds = workspaceContext?.selectedPropertyIds?.length
    ? workspaceContext.selectedPropertyIds
    : ctx.propertyId ? [ctx.propertyId] : [];
  
  // Get the base path for tab navigation
  const getBasePath = () => {
    if (params.magicId && params.password) {
      return `/s/${params.magicId}/${params.password}`;
    }
    if (params.workspaceId) {
      return `/workspace/${params.workspaceId}`;
    }
    if (params.propertySlugId) {
      return `/p/${params.propertySlugId}`;
    }
    if (params.domain) {
      return `/d/${params.domain}`;
    }
    return '';
  };
  const basePath = getBasePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState<PageBreakdown[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [groupSummary, setGroupSummary] = useState<
    Record<
      string,
      { pages: number; page_views: number; unique_visitors: number }
    >
  >({});
  const [summaryTotals, setSummaryTotals] = useState({
    pages: 0,
    page_views: 0,
    unique_visitors: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isInitialLoad = useRef(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(
    new Set(["all"])
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageViewType, setPageViewType] = useState<PageViewType>('pages');
  /** page → click totals for CTR column (clicks ÷ unique visitors) */
  const [clicksByPage, setClicksByPage] = useState<Record<string, PageClickTotals>>(
    {}
  );
  const clicksByPageRef = useRef<Record<string, PageClickTotals>>({});
  const clicksJoinCursorRef = useRef<ClicksJoinCursor>({
    offset: 0,
    total: 0,
    exhausted: true,
  });
  const clicksJoinGenRef = useRef(0);
  const clicksJoinTailRef = useRef(Promise.resolve());
  const itemsPerPage = 10;
  const batchSize = 100;

  const enqueueClicksJoin = (task: () => Promise<void>) => {
    const next = clicksJoinTailRef.current.then(task, task);
    clicksJoinTailRef.current = next.then(() => undefined, () => undefined);
  };

  // Client filtering uses live searchTerm — reset page/groups immediately so a
  // stale page index or group chip can't hide matches while debounce catches up.
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setSelectedGroups((prev) =>
      prev.size === 1 && prev.has("all") ? prev : new Set(["all"])
    );
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  // Extract entry/exit pages from dashboard data
  const entryPages = useMemo(() => {
    return (ctx.dashboardData?.metrics?.entryPages || []) as EntryExitPage[];
  }, [ctx.dashboardData]);

  const exitPages = useMemo(() => {
    return (ctx.dashboardData?.metrics?.exitPages || []) as EntryExitPage[];
  }, [ctx.dashboardData]);

  // Detail drawer state
  const selectedPage = searchParams.get("detailPage");
  const [pageDetails, setPageDetails] = useState<PagesDetailsResponse | null>(
    null
  );
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!propertyIds.length || !token) {
        setIsLoading(false);
        return;
      }

      // Soft-refresh on search so the input stays mounted and the table can
      // update in place. Full-page LoadingState unmounted the search field and
      // made results look like they never arrived.
      if (isInitialLoad.current) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setCurrentPage(1);
      // Bump the generation up front so it's in scope for the catch/finally guards
      // below — a stale request must never clear the newer request's data or flags.
      const joinGen = ++clicksJoinGenRef.current;
      try {
        const externalApiPeriod = toExternalApiPeriod(state.period || "7d");

        const filterParams: Record<string, string> = {};
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value;
          else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value;
        }

        const search = debouncedSearch || undefined;
        clicksJoinCursorRef.current = { offset: 0, total: 0, exhausted: true };
        clicksByPageRef.current = {};

        const [response, clicksResponse] = await Promise.all([
          interceptDemoAPI(
            propertyIds[0],
            () => getAggregatePagesBreakdown(propertyIds, externalApiPeriod, token, batchSize, ctx.propertyId, search, 0, filterParams),
            demoGenerators.pages
          ),
          interceptDemoAPI(
            propertyIds[0],
            () => getAggregateClicksBreakdown(propertyIds, externalApiPeriod, token, batchSize, ctx.propertyId, filterParams, search, 0),
            demoGenerators.clicks
          ).catch(() => null),
        ]);

        // A newer request (higher joinGen) may have started while this one was in
        // flight. Bail before any setState so a slower earlier fetch can't overwrite
        // the table with stale rows — which would then mismatch the newer request's
        // clicks map and make CTR no longer line up with the listed pages.
        if (joinGen !== clicksJoinGenRef.current) return;

        const rows = response.data ?? [];
        setPageData(rows);
        setTotalCount(response.meta.total_count ?? rows.length);
        setGroupSummary(response.summary.groups || {});
        setSummaryTotals({
          pages: response.summary.total_pages ?? 0,
          page_views: response.summary.total_page_views ?? 0,
          unique_visitors: response.summary.total_unique_visitors ?? 0,
        });

        const firstClickRows = clicksResponse?.data ?? [];
        const clickMap: Record<string, PageClickTotals> = {};
        mergeClicksIntoMap(clickMap, firstClickRows);
        const firstCount = firstClickRows.length;
        const firstTotal = clicksResponse?.meta.total_count ?? firstCount;
        const cursor: ClicksJoinCursor = {
          offset: firstCount,
          total: firstTotal,
          exhausted: !clicksResponse || firstCount === 0 || firstCount >= firstTotal,
        };

        clicksJoinCursorRef.current = cursor;
        clicksByPageRef.current = clickMap;
        setClicksByPage(clickMap);

        // The clicks endpoint ranks by clicks, the pages endpoint by views, so the
        // first parallel clicks batch doesn't necessarily cover the visible pages.
        // Continue walking the (bounded) clicks list to completion in the background
        // so every clicked page gets a CTR, regardless of which page batch it's on.
        if (!cursor.exhausted && token) {
          enqueueClicksJoin(() =>
            advanceClicksJoin({
              map: clicksByPageRef.current,
              cursor: clicksJoinCursorRef.current,
              propertyIds,
              period: externalApiPeriod,
              token,
              sharePropertyId: ctx.propertyId,
              filterParams,
              search,
              batchSize,
              isStale: () => joinGen !== clicksJoinGenRef.current,
              onBatch: (map, nextCursor) => {
                clicksByPageRef.current = map;
                clicksJoinCursorRef.current = nextCursor;
                setClicksByPage(map);
              },
            }),
          );
        }
      } catch (error) {
        console.error("Error fetching page stats:", error);
        // Don't clear state for a superseded request — a newer fetch owns it now.
        if (joinGen !== clicksJoinGenRef.current) return;
        setPageData([]);
        clicksByPageRef.current = {};
        clicksJoinCursorRef.current = { offset: 0, total: 0, exhausted: true };
        setClicksByPage({});
      } finally {
        // Only the latest request controls the shared loading flags; a stale one
        // finishing late must not flip them off out from under the newer fetch.
        if (joinGen === clicksJoinGenRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
          isInitialLoad.current = false;
        }
      }
    };

    fetchData();
  }, [propertyIds.join(','), token, state.period, JSON.stringify(state.filters), debouncedSearch]);

  // A page filter narrows every number to one page. "Where views land" still
  // draws the whole site with the filtered page in its own slot, and a filter
  // that matches nothing suggests the nearest page, so read the same breakdown
  // once more without the page filter (every other filter still applies).
  const activePageFilter = getActivePageFilter(state.filters);
  const [siteBreakdown, setSiteBreakdown] = useState<{
    rows: PageBreakdown[];
    totalViews: number;
    totalPages: number;
  } | null>(null);

  const sitePropertyKey = propertyIds.join(',');
  const siteFiltersKey = JSON.stringify(state.filters);
  const sitePeriod = state.period || "7d";
  const sharePropertyId = ctx.propertyId;
  useEffect(() => {
    const ids = sitePropertyKey ? sitePropertyKey.split(',') : [];
    if (!activePageFilter || !ids.length || !token) {
      setSiteBreakdown(null);
      return;
    }
    let stale = false;
    const filterParams: Record<string, string> = {};
    for (const f of JSON.parse(siteFiltersKey) as FilterClause[]) {
      if (f.field === "page") continue;
      if (f.operator === ':') filterParams[f.field] = f.value;
      else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value;
    }
    interceptDemoAPI(
      ids[0],
      () => getAggregatePagesBreakdown(ids, toExternalApiPeriod(sitePeriod), token, PAGE_BATCH_SIZE, sharePropertyId, undefined, 0, filterParams),
      demoGenerators.pages
    )
      .then((response) => {
        if (stale) return;
        setSiteBreakdown({
          rows: response.data ?? [],
          totalViews: response.summary.total_page_views ?? 0,
          totalPages: response.summary.total_pages ?? 0,
        });
      })
      .catch((error) => {
        console.error("Error fetching site-wide page stats:", error);
        if (!stale) setSiteBreakdown(null);
      });
    return () => {
      stale = true;
    };
  }, [sitePropertyKey, token, sitePeriod, siteFiltersKey, activePageFilter, sharePropertyId]);

  const applyPageFilter = (page: string) => {
    updateState({ filters: addFilter(state.filters, "page", ":", page) });
  };
  const clearPageFilter = () => {
    updateState({ filters: removeFilter(state.filters, "page") });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    const requiredItems = newPage * itemsPerPage;
    if (requiredItems > pageData.length && pageData.length < totalCount && !isFetchingMore) {
      setIsFetchingMore(true);
      const offset = pageData.length;
      // Capture the join generation now, before the request resolves. A search /
      // period / filter refresh finishing mid-flight bumps clicksJoinGenRef, which
      // must invalidate this whole callback — otherwise we'd append stale-search rows
      // and run their click join against the new cursor with the old search.
      const joinGen = clicksJoinGenRef.current;
      const externalApiPeriod = toExternalApiPeriod(state.period || "7d");
      const filterParams: Record<string, string> = {};
      for (const f of state.filters) {
        if (f.operator === ':') filterParams[f.field] = f.value;
        else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value;
      }
      const search = debouncedSearch || undefined;
      // The clicks join runs to exhaustion from the initial load, so loading more
      // pages never needs to extend it — every clicked page is already covered by
      // the (bounded) clicks walk regardless of pagination.
      getAggregatePagesBreakdown(propertyIds, externalApiPeriod, token!, batchSize, ctx.propertyId, search, offset, filterParams)
        .then((response) => {
          if (joinGen !== clicksJoinGenRef.current) return;
          const newRows = response.data ?? [];
          setPageData((prev) => [...prev, ...newRows]);
          setTotalCount(response.meta.total_count ?? (offset + newRows.length));
        })
        .catch((error) => console.error("Error fetching more pages:", error))
        .finally(() => setIsFetchingMore(false));
    }
  };

  // Fetch page details when a page is selected
  useEffect(() => {
    const fetchPageDetails = async () => {
      if (!selectedPage || !propertyIds.length || !token) {
        setPageDetails(null);
        return;
      }

      setIsLoadingDetails(true);
      try {
        const externalApiPeriod = toExternalApiPeriod(state.period || "7d");
        const filterParams: Record<string, string> = {};
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value;
          else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value;
        }
        const response = await getPagesDetails(selectedPage, externalApiPeriod, propertyIds[0], token, 20, filterParams);
        setPageDetails(response);
      } catch (error) {
        console.error("Error fetching page details:", error);
        setPageDetails(null);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchPageDetails();
  }, [selectedPage, propertyIds[0], token, state.period, JSON.stringify(state.filters)]);

  const togglePageDetails = (page: string) => {
    const params = new URLSearchParams(searchParams);
    if (selectedPage === page) {
      // Close if already open
      params.delete("detailPage");
    } else {
      // Open new page
      params.set("detailPage", page);
    }
    setSearchParams(params);
  };

  // Toggle group filter
  const toggleGroup = (group: string) => {
    const newGroups = new Set(selectedGroups);

    if (group === "all") {
      // If selecting "all", clear other selections
      setSelectedGroups(new Set(["all"]));
      setCurrentPage(1);
      return;
    }

    // Remove "all" if it's selected and we're selecting a specific group
    newGroups.delete("all");

    if (newGroups.has(group)) {
      newGroups.delete(group);
      // If no groups selected, default to "all"
      if (newGroups.size === 0) {
        newGroups.add("all");
      }
    } else {
      newGroups.add(group);
    }

    setSelectedGroups(newGroups);
    setCurrentPage(1);
  };

  // Get available groups sorted by page views
  const availableGroups = Object.entries(groupSummary)
    .sort((a, b) => b[1].page_views - a[1].page_views)
    .map(([group]) => group);

  // Filter by group chips + client search (belt-and-suspenders with backend ?search=).
  // Client filter uses the live searchTerm so rows update as you type, not only after debounce.
  const filteredData = pageData.filter((page) => {
    const groupOk =
      selectedGroups.has("all") || selectedGroups.has(page.page_group);
    const searchOk = matchesPageSearch(page.page, searchTerm, [page.page_group]);
    return groupOk && searchOk;
  });

  const isGroupFiltered = !selectedGroups.has("all");

  // Same field the Overview strip reads, so one period can't report two different
  // visitor counts on two tabs.
  const accountUniqueVisitors =
    ctx.dashboardData?.metrics?.trends?.totalSessions?.[0]?.total_sessions ?? 0;

  // Summary metrics come from the API's own totals, never from adding up rows: a visitor
  // who lands on three pages is counted in all three `unique_visitors`, so summing that
  // column overstates the period. Views and page counts are additive, so they can be
  // summed per group while the group chips are active.
  // A search narrows the list, so only the search-scoped total describes what's on
  // screen; unnarrowed, prefer the account-level count the Overview shows.
  const allSectionsUniqueVisitors = debouncedSearch
    ? summaryTotals.unique_visitors
    : accountUniqueVisitors || summaryTotals.unique_visitors;

  const { totalPageViews, totalUniqueVisitors, totalPagesCount } = (() => {
    if (isGroupFiltered) {
      const groups = Array.from(selectedGroups)
        .map((group) => groupSummary[group])
        .filter(Boolean);

      // Unique visitors: a visitor who reads two sections sits in both groups'
      // `unique_visitors`, so groups are never summed. One section reads its own
      // deduped value; every section reads the deduped summary total; any other
      // multi-section pick has no deduped figure from the API and returns null.
      const groupUniques = Object.fromEntries(
        Object.entries(groupSummary).map(([group, g]) => [group, g.unique_visitors]),
      );

      return {
        totalPageViews: groups.reduce((sum, g) => sum + g.page_views, 0),
        totalUniqueVisitors: uniqueForGroupSelection(selectedGroups, groupUniques, allSectionsUniqueVisitors),
        totalPagesCount: groups.reduce((sum, g) => sum + g.pages, 0),
      };
    }

    return {
      totalPageViews: summaryTotals.page_views,
      totalUniqueVisitors: allSectionsUniqueVisitors as number | null,
      totalPagesCount: summaryTotals.pages,
    };
  })();

  // Previous equal-length window for KPI deltas. The comparison summary is
  // site-wide, so deltas only show for the unnarrowed view: a section chip or a
  // search changes what the tile counts, and a multi-section pick shows "--".
  const previousPeriod = usePreviousPeriodMetrics({
    propertyIds,
    period: state.period || "7d",
    filters: state.filters,
    token,
  });
  const comparable = !isGroupFiltered && !debouncedSearch && !!previousPeriod.data;
  const visitorsDelta = comparable && totalUniqueVisitors !== null
    ? computePeriodDelta(totalUniqueVisitors, previousPeriod.data?.visitors)
    : null;
  const viewsDelta = comparable ? computePeriodDelta(totalPageViews, previousPeriod.data?.views) : null;
  const deltaTitle = `Compared with ${previousPeriod.phrase}`;

  const topReferrer = (() => {
    const referrerCounts: Record<string, number> = {};
    filteredData.forEach((page) => {
      page.top_sources.forEach((source) => {
        const referrer = source.utm_source || "Direct";
        referrerCounts[referrer] =
          (referrerCounts[referrer] || 0) + source.sessions;
      });
    });

    let maxReferrer = "Direct";
    let maxCount = 0;
    Object.entries(referrerCounts).forEach(([ref, count]) => {
      if (count > maxCount) {
        maxCount = count;
        maxReferrer = ref;
      }
    });
    return maxReferrer;
  })();

  const avgScrollDepth = (() => {
    if (filteredData.length === 0) return 0;
    const sum = filteredData.reduce((a, b) => a + b.avg_scroll_percent, 0);
    return Math.round(sum / filteredData.length);
  })();

  const avgSessionLength = (() => {
    if (filteredData.length === 0) return 0;
    const sum = filteredData.reduce((a, b) => a + b.avg_session_length, 0);
    return Math.round(sum / filteredData.length / 1000);
  })();

  // Sort and paginate
  const sortedData = [...filteredData].sort((a, b) => b.page_views - a.page_views);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  // When "all" groups selected, use server total_count for pagination; otherwise use filtered count
  const paginationTotal = isGroupFiltered ? sortedData.length : Math.max(totalCount, sortedData.length);


  // CTR = clicks ÷ the page's own unique visitors (per CTR_TOOLTIP). The clicks map
  // only supplies the numerator; the denominator is always the page row's own visitor
  // count, never the clicks endpoint's session count (which would inflate the rate).
  const formatCtr = (page: PageBreakdown) => {
    const clicks = clicksByPage[page.page]?.clicks ?? 0;
    const visitors = page.unique_visitors;
    if (!clicks || !visitors) return null;
    return (clicks / visitors) * 100;
  };

  const formatScrollDepth = (depth: number) => {
    return `${Math.round(depth)}%`;
  };

  // Use shared formatDuration from stringUtils for session length formatting

  const getTopReferrer = (page: PageBreakdown) => {
    if (page.top_sources.length === 0) return "Direct";
    const topSource = page.top_sources[0];
    return topSource.utm_source || "Direct";
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <AnalyticsTabBar basePath={basePath} className="mb-6" />
        <PagesLoading />
      </div>
    );
  }

  const landingSegments = activePageFilter
    ? siteBreakdown
      ? buildFilteredLandingSegments(
          siteBreakdown.rows,
          siteBreakdown.totalViews,
          siteBreakdown.totalPages,
          activePageFilter,
          totalPageViews,
        )
      : []
    : buildLandingSegments(sortedData, totalPageViews, totalPagesCount);

  // Subline: "acme.com, Sep 19 to Sep 25", plus what the filter is doing.
  const summary = getTrafficSummary(ctx.dashboardData?.metrics, false);
  const scopeLabel = ctx.dashboardData?.metrics?.domain || ctx.dashboardData?.currentDomain || "";
  const baseMeta = [scopeLabel, formatRangeLabel(summary.rangeStart, summary.rangeEnd, state.period || "7d")]
    .filter(Boolean)
    .join(", ");
  const siteViews = siteBreakdown?.totalViews ?? 0;
  const filterLabel = activePageFilter ? `Page is ${activePageFilter}` : "";
  // A page filter matching nothing: no rows and no views, before any search narrows it.
  const filterHasNoResults =
    !!activePageFilter && !debouncedSearch && !searchTerm && pageData.length === 0 && totalPageViews === 0;
  const nearestPage = filterHasNoResults && siteBreakdown
    ? findNearestPage(activePageFilter!, siteBreakdown.rows)
    : null;

  const verdict: PagesVerdictProps = filterHasNoResults
    ? {
        kind: "noResults",
        filterLabel,
        meta: `${baseMeta}${siteBreakdown ? ` · 0 of ${formatNumeric(siteViews)} views on the site` : ""}`,
      }
    : activePageFilter
      ? {
          kind: "filtered",
          page: activePageFilter,
          views: totalPageViews,
          share: siteViews > 0 ? (totalPageViews / siteViews) * 100 : 0,
          meta: `${baseMeta} · filtered to ${activePageFilter}`,
        }
      : {
          kind: "all",
          totalViews: totalPageViews,
          totalPages: totalPagesCount,
          topPage: sortedData[0] ? sortedData[0].page || "/" : null,
          topShare: sortedData[0] && totalPageViews > 0 ? (sortedData[0].page_views / totalPageViews) * 100 : 0,
          meta: baseMeta,
        };

  const entryExitRows = (() => {
    if (pageViewType === "pages") return { rows: [] as EntryExitPage[], total: 0, count: 0 };
    const sourceData = pageViewType === "entry" ? entryPages : exitPages;
    const filtered = sourceData
      .filter((p) => matchesPageSearch(p.page, searchTerm))
      .sort((a, b) => Number(b.entry_count) - Number(a.entry_count));
    // Entries and exits are events, so they add up across pages.
    const total = filtered.reduce((sum, p) => sum + Number(p.entry_count), 0);
    return {
      rows: filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
      total,
      count: filtered.length,
    };
  })();

  const paginationCount = pageViewType === "pages" ? paginationTotal : entryExitRows.count;
  const paginationPages = Math.ceil(paginationCount / itemsPerPage);

  const listToolbar = (
    <PagesListToolbar
      view={pageViewType}
      onViewChange={(v) => {
        setPageViewType(v);
        setCurrentPage(1);
      }}
      search={searchTerm}
      onSearchChange={handleSearchChange}
    />
  );

  const renderDetails = () =>
    isLoadingDetails ? (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
        <Skeleton className="h-24 w-full" />
      </div>
    ) : pageDetails ? (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:flex sm:flex-wrap">
          {[
            { label: "Total views", value: formatNumeric(pageDetails.data.metrics.page_views) },
            { label: "Unique visitors", value: formatNumeric(pageDetails.data.metrics.unique_visitors) },
            { label: "Time on page", value: formatDuration(pageDetails.data.metrics.avg_session_length / 1000) },
            { label: "Scroll depth", value: formatScrollDepth(pageDetails.data.metrics.avg_scroll_percent) },
            { label: "Bounce rate", value: `${(pageDetails.data.metrics.bounce_rate * 100).toFixed(1)}%` },
          ].map((m) => (
            <div key={m.label} className="flex flex-col gap-0.5">
              <span className="text-xs leading-4 text-muted-foreground">{m.label}</span>
              <span className="text-sm font-medium leading-5 text-foreground tabular-nums">{m.value}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
          {pageDetails.data.utm_breakdown.length > 0 && (
            <TrafficSourcesList
              items={(() => {
                const aggregated = new Map<
                  string,
                  {
                    primary: string;
                    secondary: string | null;
                    secondaryFull: string | null;
                    value: number;
                    share: number;
                  }
                >();
                for (const utm of pageDetails.data.utm_breakdown) {
                  const { primary, secondary, secondaryFull, groupKey } = formatUtmLabel(utm);
                  const existing = aggregated.get(groupKey);
                  if (existing) {
                    existing.value += utm.page_views;
                    existing.share += utm.share;
                  } else {
                    aggregated.set(groupKey, {
                      primary,
                      secondary,
                      secondaryFull,
                      value: utm.page_views,
                      share: utm.share,
                    });
                  }
                }
                return Array.from(aggregated.values())
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 5);
              })()}
            />
          )}

          <div className="min-w-0 space-y-4">
            {pageDetails.data.device_breakdown.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-medium leading-4 text-foreground">Devices</h4>
                <div className="flex flex-wrap items-center gap-2">
                  {pageDetails.data.device_breakdown.map((device, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 rounded-md bg-card px-2 py-1 ring-1 ring-foreground/[0.08]"
                    >
                      <span className="text-xs capitalize text-muted-foreground">{device.device}</span>
                      <span className="text-xs font-medium text-foreground tabular-nums">
                        {(device.share * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pageDetails.data.country_breakdown.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-medium leading-4 text-foreground">Countries</h4>
                <div className="space-y-1.5">
                  {pageDetails.data.country_breakdown.slice(0, 5).map((country, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-[13px] text-foreground">{country.country}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-foreground tabular-nums">
                          {formatNumeric(country.page_views)}
                        </span>
                        <span className="w-12 text-right text-xs text-muted-foreground tabular-nums">
                          {(country.share * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ) : (
      <p className="py-8 text-center text-[13px] text-muted-foreground">Failed to load page details</p>
    );

  return (
    <TooltipProvider>
      <div className="w-full">
        {/* Analytics Tab Navigation */}
        <AnalyticsTabBar basePath={basePath} className="mb-6" />

        <div className="flex flex-col gap-4">
          <PagesVerdict {...verdict} />

          {/* One well, raised cards (White signal v2) */}
          <div className="well flex flex-col gap-2">
            {filterHasNoResults ? (
              <Card className="gap-0 py-0">
                {listToolbar}
                <div className="responsive-table-wrapper">
                  <Table>
                    <PagesTableHead />
                  </Table>
                </div>
                <PagesFilterEmpty
                  filterLabel={filterLabel}
                  periodNoun={periodNoun(state.period || "7d")}
                  nearest={nearestPage}
                  onApplyNearest={applyPageFilter}
                  onClear={clearPageFilter}
                />
              </Card>
            ) : (
              <>
            {/* KPI tiles + where views land */}
            <PagesSummaryCard
              kpis={[
                {
                  label: totalUniqueVisitors === null ? "Unique visitors · pick one section or all" : "Unique visitors",
                  value: totalUniqueVisitors === null ? "--" : formatNumeric(totalUniqueVisitors),
                  title: totalUniqueVisitors === null ? "Not available for a multi-section selection: visitors who read several sections can't be deduplicated. Select one section, or all." : undefined,
                  delta: visitorsDelta,
                  deltaTitle,
                },
                { label: "Total views", value: formatNumeric(totalPageViews), delta: viewsDelta, deltaTitle },
                { label: "Pages", value: formatNumeric(totalPagesCount) },
                { label: "Avg scroll depth", value: `${avgScrollDepth}%` },
                { label: "Avg time on page", value: formatDuration(avgSessionLength) },
                { label: "Top source", value: topReferrer || "Direct", title: topReferrer || "Direct" },
              ]}
            >
              <WhereViewsLand
                segments={landingSegments}
                totalViews={activePageFilter ? siteViews : totalPageViews}
                filteredPage={activePageFilter}
              />
            </PagesSummaryCard>

            {/* Pages table */}
            <Card className="gap-0 py-0">
              {listToolbar}

              {pageViewType === "pages" && availableGroups.length > 0 && (
                <SectionChipRow>
                  <SectionChip active={selectedGroups.has("all")} onClick={() => toggleGroup("all")}>
                    All {formatNumeric(summaryTotals.pages || pageData.length)}
                  </SectionChip>
                  {availableGroups.map((group) => (
                    <SectionChip key={group} active={selectedGroups.has(group)} onClick={() => toggleGroup(group)}>
                      {sentenceLabel(group)} {groupSummary[group].pages}
                    </SectionChip>
                  ))}
                </SectionChipRow>
              )}

              <div className="responsive-table-wrapper">
                {/* Entry/Exit Pages Table */}
                {pageViewType !== "pages" && (
                  <Table>
                    <TableHeader className="[&_tr]:border-b-0">
                      <TableRow className={reportTable.headRow}>
                        <TableHead className={cn(reportTable.th, "min-w-[200px]")}>Page</TableHead>
                        <TableHead className={cn(reportTable.th, "w-[100px] text-right")}>
                          {pageViewType === "entry" ? "Entries" : "Exits"}
                        </TableHead>
                        <TableHead className={cn(reportTable.th, "hidden w-[120px] text-right sm:table-cell")}>Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entryExitRows.rows.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={3} className={reportTable.empty}>
                            {searchTerm ? "No pages match your search" : `No ${pageViewType === "entry" ? "landing" : "exit"} page data for this range yet`}
                          </TableCell>
                        </TableRow>
                      ) : (
                        entryExitRows.rows.map((page, index) => {
                          const count = Number(page.entry_count);
                          const percentage = entryExitRows.total > 0 ? (count / entryExitRows.total) * 100 : 0;
                          return (
                            <TableRow key={`${page.page}-${index}`} className={reportTable.row}>
                              <TableCell className={cn(reportTable.td, "max-w-0")} title={page.page}>
                                <span className="block truncate text-sm font-medium text-foreground">{page.page || "/"}</span>
                              </TableCell>
                              <TableCell className={cn(reportTable.td, reportTable.num)}>{formatNumeric(count)}</TableCell>
                              <TableCell className={cn(reportTable.td, "hidden sm:table-cell")}>
                                <ShareMeter percent={percentage} />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                )}

                {/* Top pages table */}
                {pageViewType === "pages" && (
                  <PagesTopTable
                    rows={paginatedData.map((page) => ({
                      page: page.page,
                      group: page.page_group,
                      views: page.page_views,
                      visitors: page.unique_visitors,
                      ctr: formatCtr(page),
                      referrer: getTopReferrer(page),
                      scrollPercent: page.avg_scroll_percent,
                      timeOnPageSeconds: page.avg_session_length / 1000,
                    }))}
                    expandedPage={selectedPage}
                    onToggle={togglePageDetails}
                    renderDetails={renderDetails}
                    emptyText={searchTerm ? "No pages match your search" : "No page data for this range yet"}
                  />
                )}
              </div>

              {paginationCount > 0 && (
                <ReportPagination
                  label={
                    activePageFilter && pageViewType === "pages" && paginationPages <= 1
                      ? `${formatNumeric(paginationCount)} ${paginationCount === 1 ? "page matches" : "pages match"} the filter`
                      : undefined
                  }
                  from={(currentPage - 1) * itemsPerPage + 1}
                  to={Math.min(currentPage * itemsPerPage, paginationCount)}
                  total={paginationCount}
                  noun="pages"
                  busy={isFetchingMore}
                  onPrevious={() => handlePageChange(Math.max(1, currentPage - 1))}
                  onNext={() => handlePageChange(Math.min(paginationPages, currentPage + 1))}
                  previousDisabled={currentPage === 1}
                  nextDisabled={currentPage === paginationPages || isFetchingMore}
                />
              )}
            </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
